import { useEffect } from 'react'
import { Navigate, Outlet, RouteObject, useLocation, useRoutes } from 'react-router-dom'
import { useContext } from 'use-context-selector'

import {
  ActivateELockPage,
  ActivateOrderPage,
  AddEmployeePage,
  CreateOrderPage,
  EditContactPage,
  EditEmployeePage,
  EditPassportPage,
  EditRequisitesPage,
  EmployeePage,
  OrderItemPage,
  OrderPage,
  OrderPaymentPage,
  ProfilePage,
  PvzEditAreaPage,
  PvzEditTimetablePage,
  PvzItemPage,
  PvzPage,
  TicketItemPage,
  TicketPage,
} from 'sections/LKSection'
import { LKGeneralLayout } from 'sections/LKSection/LKGeneralLayout/LKGeneralLayout'
import { PvzSignboardPage } from 'sections/LKSection/modules/PvzModule/pages/PvzSignboardPage/PvzSignboardPage'
import { LoginPage } from 'sections/LKSection/modules/UserModule/pages/LoginPage/LoginPage'
import { RegistrationPage } from 'sections/LKSection/modules/UserModule/pages/RegistrationPage/RegistrationPage'
import {
  AppsPage,
  BankingServicesPage,
  ConferencesPage,
  DownloadsPage,
  ELockContractorsPage,
  ELockPage,
  KnowledgeBasePage,
  ListSignBoardsContractorsPage,
  ListSignBoardsSortingCentersPage,
  MapPage,
  PromoPage,
  RatingPage,
  SignBoardPage,
  SimplifiedConditionsPage,
  VideoConnectionPage,
} from 'sections/PromoSection'
import { LoginPageGe } from 'sections/PromoSection/pages/PromoPage/PromoPageGe/pages/LoginPage/LoginPageGe'
import { PromoPageGe } from 'sections/PromoSection/pages/PromoPage/PromoPageGe/PromoPageGe'
import { PrivacyPolicy } from 'sections/PromoSection/pages/PromoPage/Sections/PrivacyPolicy/PrivacyPolicy'
import { PromoGeneralLayout } from 'sections/PromoSection/PromoGeneralLayout/PromoGeneralLayout'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { AuthRoutesEnum, LKRoutesEnum, PromoGeRoutesEnum, PromoRoutesEnum } from 'shared/constants/routes'

import { MovingPvzPage } from './sections/LKSection/modules/PvzModule/pages/MovingPvzPage/MovingPvzPage'
import { setUserRole } from './states/UserState/reducer'
import { UserStateContext } from './states/UserState/UserStateProvider'

export const RouteList = () => {
  const ctx = useContext(UserStateContext)
  const location = useLocation()

  useEffect(() => {
    try {
      const pathname = decodeURIComponent(location.pathname)
      const token = JSON.parse(pathname?.slice(2))?.token

      if (token) {
        ctx.dispatch(setUserRole(token))
      }
    } catch (error) {
      // обработчик не нужен, но try необходим
    }
  }, [])

  const lkRoutes: RouteObject = {
    path: '/lk',
    element: <LKGeneralLayout />,
    children: [
      {
        path: LKRoutesEnum.orders,
        element: <Outlet />,
        children: [
          { path: LKRoutesEnum.orders, element: <OrderPage /> },
          { path: ':orderId', element: <OrderItemPage /> },
          { path: ':orderId/activate', element: <ActivateOrderPage /> },
          { path: ':orderId/activate-elock', element: <ActivateELockPage /> },
          { path: `${LKRoutesEnum.orders}/:orderId/error`, element: <OrderItemPage hasErrorWithPayment /> },
          { path: LKRoutesEnum.orderCreate, element: <CreateOrderPage /> },
          { path: `${LKRoutesEnum.orderPayment}/:orderId`, element: <OrderPaymentPage /> },
        ],
      },
      {
        path: LKRoutesEnum.pvz,
        element: <Outlet />,
        children: [
          { path: LKRoutesEnum.pvz, element: <PvzPage /> },
          { path: ':pvzId', element: <PvzItemPage /> },
          { path: ':pvzId/edit-area', element: <PvzEditAreaPage /> },
          { path: ':pvzId/edit-timetable', element: <PvzEditTimetablePage /> },
          { path: ':pvzId/moving', element: <MovingPvzPage /> },
          { path: ':pvzId/signboard', element: <PvzSignboardPage /> },
        ],
      },
      {
        path: LKRoutesEnum.tickets,
        element: <Outlet />,
        children: [
          { path: LKRoutesEnum.tickets, element: <TicketPage /> },
          { path: ':ticketId', element: <TicketItemPage /> },
        ],
      },
      {
        path: LKRoutesEnum.employees,
        element: <Outlet />,
        children: [
          { path: LKRoutesEnum.employees, element: <EmployeePage /> },
          { path: `${LKRoutesEnum.employees}/:pvzId`, element: <EmployeePage /> },
          { path: `${LKRoutesEnum.addEmployee}/:pvzId`, element: <AddEmployeePage /> },
          { path: `${LKRoutesEnum.editEmployee}/:pvzId/:userId`, element: <EditEmployeePage /> },
        ],
      },
      { path: LKRoutesEnum.profile, element: <ProfilePage /> },
      { path: LKRoutesEnum.editRequisites, element: <EditRequisitesPage /> },
      { path: LKRoutesEnum.editPassport, element: <EditPassportPage /> },
      { path: LKRoutesEnum.editContact, element: <EditContactPage /> },
    ],
  }

  const filteredRoutes = isGeDomain()
    ? lkRoutes.children?.filter((route) => route.path !== LKRoutesEnum.pvz) || []
    : lkRoutes.children

  lkRoutes.children = filteredRoutes

  const routesConfig = isGeDomain()
    ? [
        {
          path: PromoGeRoutesEnum.root,
          element: <PromoGeneralLayout />,
          children: [
            { path: PromoGeRoutesEnum.root, element: <PromoPageGe /> },
            { path: PromoGeRoutesEnum.apps, element: <AppsPage /> },
            { path: AuthRoutesEnum.login, element: <LoginPage /> },
            { path: AuthRoutesEnum.registration, element: <RegistrationPage /> },
          ],
        },
        {
          path: PromoGeRoutesEnum.root,
          element: <PromoGeneralLayout />,
          children: [{ path: PromoGeRoutesEnum.login, element: <LoginPageGe /> }],
        },
        lkRoutes,
        { path: '/lk/*', element: <Navigate to={LKRoutesEnum.orders} /> },
        { path: '/*', element: <Navigate to={PromoGeRoutesEnum.root} /> },
      ]
    : [
        {
          path: PromoRoutesEnum.root,
          element: <PromoGeneralLayout />,
          children: [
            { path: PromoRoutesEnum.root, element: <PromoPage /> },
            { path: PromoRoutesEnum.banners, element: <DownloadsPage /> },
            { path: PromoRoutesEnum.signboard, element: <SignBoardPage /> },
            { path: PromoRoutesEnum.listSignBoardsContractors, element: <ListSignBoardsContractorsPage /> },
            { path: PromoRoutesEnum.listSignBoardsSortingCenters, element: <ListSignBoardsSortingCentersPage /> },
            { path: PromoRoutesEnum.listELockContractors, element: <ELockContractorsPage /> },
            { path: PromoRoutesEnum.eLock, element: <ELockPage /> },
            { path: PromoRoutesEnum.videoConnection, element: <VideoConnectionPage /> },
            { path: PromoRoutesEnum.conferences, element: <ConferencesPage /> },
            { path: PromoRoutesEnum.rating, element: <RatingPage /> },
            { path: PromoRoutesEnum.apps, element: <AppsPage /> },

            { path: AuthRoutesEnum.registration, element: <RegistrationPage /> },
            { path: PromoRoutesEnum.bankingServices, element: <BankingServicesPage /> },
            { path: PromoRoutesEnum.simplifiedConditions, element: <SimplifiedConditionsPage /> },
            { path: PromoRoutesEnum.map, element: <MapPage /> },
          ],
        },
        { path: AuthRoutesEnum.login, element: <LoginPage /> },
        {
          path: PromoRoutesEnum.root,
          element: <PrivacyPolicy />,
          children: [{ path: PromoRoutesEnum.privacyPolicy, element: <PrivacyPolicy /> }],
        },
        {
          path: PromoRoutesEnum.root,
          element: <KnowledgeBasePage />,
          children: [{ path: PromoRoutesEnum.knowledgeBase, element: <KnowledgeBasePage /> }],
        },
        lkRoutes,
        { path: '/*', element: <Navigate to={PromoRoutesEnum.root} /> },
        { path: '/lk/*', element: <Navigate to={LKRoutesEnum.orders} /> },
      ]

  const Routes = useRoutes(routesConfig)

  return Routes
}
